import React, { useCallback, useState } from 'react';

import apiClient from '../services/api';
import useResource from '../services/useResource';
import { User } from '../types/api';
import './AdminPage.css';

interface StorageInfo {
  media_total: { bytes: number; files: number };
  publish_tmp: { bytes: number; files: number };
  temp_media: { bytes: number; files: number };
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function AdminPage() {
  const { data: users, loading, error, reload } = useResource<User[]>(
    () => apiClient.get<User[]>('/accounts/users/'),
    [],
    { initial: [] },
  );

  const { data: storage, reload: reloadStorage } = useResource<StorageInfo | null>(
    () => apiClient.get<StorageInfo>('/accounts/storage/'),
    [],
    { initial: null },
  );

  const [purging, setPurging] = useState(false);
  const handlePurge = useCallback(async () => {
    if (!window.confirm('Purge all temporary media files?')) return;
    setPurging(true);
    try {
      await apiClient.delete('/accounts/storage/');
      reloadStorage();
    } catch (err: any) {
      setFormError(err?.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  }, [reloadStorage]);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setCreating(true);
    try {
      await apiClient.post('/accounts/users/', { username, email, password });
      setUsername('');
      setEmail('');
      setPassword('');
      reload();
    } catch (err: any) {
      setFormError(err?.body?.error || err?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: number, name: string): Promise<void> {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/accounts/users/${id}/`);
      reload();
    } catch (err: any) {
      setFormError(err?.body?.error || err?.message || 'Failed to delete user');
    }
  }

  const errorMessage = formError || error?.message;

  return (
    <div className="admin-page">
      <h2>Admin</h2>

      <section className="admin-section panel">
        <h3>Create user</h3>
        {errorMessage && <div className="banner banner--error">{errorMessage}</div>}
        <form className="admin-form" onSubmit={handleCreate}>
          <label className="field">
            <span className="field-label">Username</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span className="field-label">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </section>

      <section className="admin-section panel">
        <h3>Storage</h3>
        {storage && (
          <ul className="admin-storage-list">
            <li>Media total: <strong>{formatBytes(storage.media_total.bytes)}</strong> ({storage.media_total.files} files)</li>
            <li>Publish temp: <strong>{formatBytes(storage.publish_tmp.bytes)}</strong> ({storage.publish_tmp.files} files)</li>
            <li>Processing temp: <strong>{formatBytes(storage.temp_media.bytes)}</strong> ({storage.temp_media.files} files)</li>
          </ul>
        )}
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={handlePurge}
          disabled={purging || (storage?.publish_tmp.files === 0 && storage?.temp_media.files === 0)}
        >
          {purging ? 'Purging…' : 'Purge temp files'}
        </button>
      </section>

      <section className="admin-section">
        <h3>Users ({users.length})</h3>
        {loading && <p className="muted">Loading…</p>}
        <ul className="admin-user-list">
          {users.map((u) => (
            <li key={u.id} className="admin-user-row">
              <div className="admin-user-info">
                <span className="admin-user-name">{u.username}</span>
                {u.email && <span className="muted">{u.email}</span>}
                {u.is_staff && <span className="badge">admin</span>}
              </div>
              <div className="admin-user-actions">
                {!u.is_staff && (
                  <button className="btn btn--danger btn--sm" onClick={() => handleDelete(u.id, u.username)}>
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
